import { motion, AnimatePresence } from "framer-motion"
import {X, Minus, Plus, ShoppingBag, Trash2} from 'lucide-react'
import { useCart } from "@/context/CartContext"
import { useLang } from "@/context/LanguageContext"
import { useState, useEffect } from "react"
import toast from "react-hot-toast"

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, totalPrice, clearCart } = useCart()
  const { t, lang } = useLang()
  const [checkoutStep, setCheckoutStep] = useState<"cart" | "form">("cart")
  const [formData, setFormData] = useState({ name: "", email: "", address: "", city: "", zip: "", country: "France" })

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0)

  // Zones: France, Reste de l'Union européenne, Suisse
  const euCountries = [
    "Allemagne", "Autriche", "Belgique", "Bulgarie", "Chypre", "Croatie",
    "Danemark", "Espagne", "Estonie", "Finlande", "Grèce", "Hongrie",
    "Irlande", "Italie", "Lettonie", "Lituanie", "Luxembourg", "Malte",
    "Pays-Bas", "Pologne", "Portugal", "République tchèque", "Roumanie",
    "Slovaquie", "Slovénie", "Suède"
  ]

  const getZone = (country: string): "France" | "EU" | "Suisse" => {
    if (country === "France") return "France"
    if (country === "Suisse") return "Suisse"
    return "EU"
  }

  const getShippingCost = (country: string, qty: number): number => {
    const zone = getZone(country)
    if (zone === "France") {
      return qty <= 1 ? 1.90 : 3.50
    }
    if (zone === "Suisse") {
      return qty <= 1 ? 1.50 : 2.50
    }
    // Reste de l'Union européenne
    return qty <= 1 ? 2.50 : 4.90
  }

  const [pickupInStore, setPickupInStore] = useState(false)

  const shippingCost = pickupInStore ? 0 : (formData.country ? getShippingCost(formData.country, totalQuantity) : null)

  const countryOptions = [
    { group: "—", items: ["France", "Suisse"] },
    { group: t("Union européenne", "European Union"), items: euCountries.sort() }
  ]
  
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = "fixed"
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = "0"
      document.body.style.right = "0"
      document.body.style.overflow = "hidden"
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ""
      document.body.style.top = ""
      document.body.style.left = ""
      document.body.style.right = ""
      document.body.style.overflow = ""
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1)
      }
    }
    return () => {
      const scrollY = document.body.style.top
      document.body.style.position = ""
      document.body.style.top = ""
      document.body.style.left = ""
      document.body.style.right = ""
      document.body.style.overflow = ""
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1)
      }
    }
  }, [isOpen])

  const handleCheckout = () => {
    if (items.length === 0) return
    setCheckoutStep("form")
  }

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    try {
      // Call Deno function to create Stripe Checkout session
      const response = await fetch("https://api.lumi.new/v1/functions/p427971759406342144/createCheckout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Origin": window.location.origin,
        },
        body: JSON.stringify({
          items: items.map(item => ({
            product: {
              id: item.product.id,
              name: item.product.sizes.length > 1 ? `${item.product.name} \u2014 ${item.size}` : item.product.name,
              price: item.product.price,
            },
            quantity: item.quantity,
          })),
          shippingCost: shippingCost || 0,
          country: formData.country,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Failed to create checkout session")
      }

      const { url } = await response.json()
      
      if (!url) {
        throw new Error("No checkout URL returned")
      }
      
      console.log("Redirecting to Stripe Checkout:", url)
      
      // Redirect immediately to Stripe Checkout
      window.location.href = url
      
    } catch (error: any) {
      console.error("Erreur lors du paiement:", error)
      toast.error(
        error?.message || t("Erreur lors du paiement", "Payment error"),
        {
          duration: 5000,
          style: {
            background: "#FFFFFF",
            color: "#82391F",
            border: "1px solid rgba(225, 89, 63, 0.2)",
          },
          iconTheme: {
            primary: "#E1593F",
            secondary: "#FFFFFF",
          },
        }
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setCheckoutStep("cart")
  }

  const formatPrice = (price: number) => {
    return price.toFixed(2).replace(".", ",") + " \u20AC"
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 w-full max-w-md border-l z-50 flex flex-col overflow-hidden"
            style={{ backgroundColor: "#F8F2EB", borderColor: "rgba(130, 57, 31, 0.1)", height: "100dvh" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}>
              <div className="flex items-center gap-3">
                <ShoppingBag size={18} strokeWidth={1.5} style={{ color: "#E1593F" }} />
                <h2 className="text-lg" style={{ color: "#82391F", letterSpacing: "4px" }}>
                  {checkoutStep === "cart" ? t("Votre Panier", "Your Cart") : t("Finaliser", "Checkout")}
                </h2>
              </div>
              <button onClick={handleClose} className="transition-colors" style={{ color: "rgba(13, 12, 11, 0.3)" }}
                onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(13, 12, 11, 0.3)")}
              >
                <X size={20} />
              </button>
            </div>

            {checkoutStep === "cart" ? (
              <>
                {/* Items */}
                <div className="flex-1 overflow-y-auto p-6" style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
                  {items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <ShoppingBag size={40} strokeWidth={1} style={{ color: "rgba(13, 12, 11, 0.08)" }} className="mb-4" />
                      <p style={{ color: "rgba(13, 12, 11, 0.35)", fontSize: "17px" }}>{t("Votre panier est vide", "Your cart is empty")}</p>
                      <p className="mt-2" style={{ color: "rgba(13, 12, 11, 0.2)", fontSize: "15px" }}>{t("Découvrez notre collection", "Discover our collection")}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {items.map(item => (
                        <motion.div
                          key={item.product.id + item.size}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: 50 }}
                          className="flex gap-4"
                        >
                          <div className="w-20 h-20 flex-shrink-0 overflow-hidden" style={{ backgroundColor: "#F0E8DE" }}>
                            <img
                              src={item.product.image}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 style={{ color: "#82391F", fontSize: "17px" }}>
                                  {item.product.name}
                                </h4>
                                <p className="text-[10px] tracking-wider uppercase" style={{ color: "rgba(225, 89, 63, 0.5)" }}>
                                  {item.product.category} \u2022 {item.size}
                                </p>
                              </div>
                              <button
                                onClick={() => removeItem(item.product.id, item.size)}
                                className="ml-2 transition-colors"
                                style={{ color: "rgba(13, 12, 11, 0.2)" }}
                                onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                                onMouseLeave={e => (e.currentTarget.style.color = "rgba(13, 12, 11, 0.2)")}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-3 border" style={{ borderColor: "rgba(130, 57, 31, 0.1)" }}>
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.size, item.quantity - 1, lang)}
                                  className="w-8 h-8 flex items-center justify-center transition-colors"
                                  style={{ color: "rgba(13, 12, 11, 0.3)" }}
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="text-xs w-4 text-center" style={{ color: "#0D0C0B" }}>{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.size, item.quantity + 1, lang)}
                                  className="w-8 h-8 flex items-center justify-center transition-colors"
                                  style={{ color: "rgba(13, 12, 11, 0.3)" }}
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                              <span style={{ fontSize: "17px" }}>
                                {item.product.originalPrice ? (
                                  <>
                                    <span style={{ color: "rgba(13, 12, 11, 0.3)", textDecoration: "line-through", marginRight: "6px", fontSize: "14px" }}>
                                      {formatPrice(item.product.originalPrice * item.quantity)}
                                    </span>
                                    <span style={{ color: "#E1593F", fontWeight: 600 }}>
                                      {formatPrice(item.product.price * item.quantity)}
                                    </span>
                                  </>
                                ) : (
                                  <span style={{ color: "#0D0C0B" }}>
                                    {formatPrice(item.product.price * item.quantity)}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                  <div className="p-6 border-t space-y-4" style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs tracking-[0.15em] uppercase" style={{ color: "rgba(13, 12, 11, 0.4)" }}>Total</span>
                      <span className="text-2xl" style={{ color: "#82391F" }}>
                        {formatPrice(totalPrice)}
                      </span>
                    </div>
                    <p className="text-[10px] text-center" style={{ color: "rgba(13, 12, 11, 0.25)", fontStyle: "italic" }}>
                      {t("Frais de livraison calculés à l'étape suivante", "Shipping costs calculated at next step")}
                    </p>
                    <button
                      onClick={handleCheckout}
                      className="w-full py-4 text-white text-xs tracking-[0.2em] uppercase transition-colors duration-300"
                      style={{ backgroundColor: "#E1593F" }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#C4462E")}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#E1593F")}
                    >
                      {t("Procéder au paiement", "Proceed to checkout")}
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Checkout form */
              <form onSubmit={handleSubmitOrder} className="flex-1 flex flex-col overflow-hidden min-h-0">
                <div className="flex-1 overflow-y-auto p-6 space-y-5" style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
                  <button
                    type="button"
                    onClick={() => setCheckoutStep("cart")}
                    className="text-xs tracking-wider uppercase transition-colors mb-2"
                    style={{ color: "rgba(225, 89, 63, 0.6)" }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(225, 89, 63, 0.6)")}
                  >
                    {t("\u2190 Retour au panier", "\u2190 Back to cart")}
                  </button>

                  {[
                    { label: t("Nom complet", "Full name"), key: "name" as const, type: "text", placeholder: "Marie Martin" },
                    { label: "Email", key: "email" as const, type: "email", placeholder: "marie@exemple.fr" },
                    { label: t("Adresse", "Address"), key: "address" as const, type: "text", placeholder: "12 Rue de la Paix" },
                    { label: t("Ville", "City"), key: "city" as const, type: "text", placeholder: "Paris" },
                    { label: t("Code postal", "Postal code"), key: "zip" as const, type: "text", placeholder: "75001" },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="text-[10px] tracking-[0.2em] uppercase block mb-2" style={{ color: "rgba(13, 12, 11, 0.4)" }}>
                        {field.label}
                      </label>
                      <input
                        required
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.key]}
                        onChange={e => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-white border px-4 py-3 focus:outline-none transition-colors placeholder:text-gray-300"
                        style={{
                          borderColor: "rgba(130, 57, 31, 0.1)",
                          color: "#0D0C0B",
                          fontSize: "17px",
                        }}
                        onFocus={e => (e.currentTarget.style.borderColor = "rgba(225, 89, 63, 0.4)")}
                        onBlur={e => (e.currentTarget.style.borderColor = "rgba(130, 57, 31, 0.1)")}
                      />
                    </div>
                  ))}

                  {/* Country dropdown */}
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase block mb-2" style={{ color: "rgba(13, 12, 11, 0.4)" }}>
                      {t("Pays", "Country")}
                    </label>
                    <select
                      required
                      value={formData.country}
                      onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full bg-white border px-4 py-3 focus:outline-none transition-colors appearance-none cursor-pointer"
                      style={{
                        borderColor: "rgba(130, 57, 31, 0.1)",
                        color: "#0D0C0B",
                        fontSize: "17px",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 16px center",
                      }}
                      onFocus={e => (e.currentTarget.style.borderColor = "rgba(225, 89, 63, 0.4)")}
                      onBlur={e => (e.currentTarget.style.borderColor = "rgba(130, 57, 31, 0.1)")}
                    >
                      {countryOptions.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.items.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Shipping notice */}
                  <div className="px-4 py-3" style={{ backgroundColor: "rgba(225, 89, 63, 0.06)", border: "1px solid rgba(225, 89, 63, 0.15)" }}>
                    <p style={{ color: "rgba(13, 12, 11, 0.55)", fontSize: "12px", lineHeight: "1.6" }}>
                      {t(
                        "Nous expédions actuellement en Europe et en Suisse. La livraison hors Europe (dont les États-Unis) n'est pas disponible pour le moment, en raison des nouvelles règles douanières américaines sur les petits colis.",
                        "We currently ship within Europe and Switzerland. Shipping outside Europe (including the US) is unavailable for now, due to recent US customs rules on low-value parcels."
                      )}
                    </p>
                  </div>

                  {/* Retrait sur place — TEMPORAIRE: libellé raccourci (sans « (Champel) ») + bloc plus compact/discret. Fonctionnement inchangé (coche = livraison 0). */}
                  <label className="flex items-start gap-2 px-3 py-2 cursor-pointer" style={{ backgroundColor: "rgba(225, 89, 63, 0.06)", border: "1px solid rgba(225, 89, 63, 0.15)" }}>
                    <input
                      type="checkbox"
                      checked={pickupInStore}
                      onChange={e => setPickupInStore(e.target.checked)}
                      className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 cursor-pointer accent-current"
                      style={{ accentColor: "#E1593F" }}
                    />
                    <span style={{ color: "rgba(13, 12, 11, 0.6)", fontSize: "12px", lineHeight: "1.4" }}>
                      {t(
                        "Retrait sur place à Genève, je viens chercher ma commande",
                        "Local pickup in Geneva, I'll collect my order myself"
                      )}
                    </span>
                  </label>

                  {/* Order summary */}
                  <div className="pt-4 border-t" style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: "rgba(13, 12, 11, 0.35)" }}>{t("Récapitulatif", "Summary")}</p>
                    {items.map(item => (
                      <div key={item.product.id + item.size} className="flex justify-between text-xs mb-2">
                        <span style={{ color: "rgba(13, 12, 11, 0.4)" }}>{item.product.name} ({item.size}) x{item.quantity}</span>
                        <span style={{ color: "rgba(13, 12, 11, 0.6)" }}>{formatPrice(item.product.price * item.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs mt-2" style={{ borderColor: "rgba(130, 57, 31, 0.06)" }}>
                      <span style={{ color: "rgba(13, 12, 11, 0.4)" }}>
                        {pickupInStore
                          ? t("Retrait à Genève", "Geneva pickup")
                          : `${t("Livraison (sans suivi)", "Shipping (no tracking)")} (${formData.country})`
                        }
                      </span>
                      <span style={{ color: "rgba(13, 12, 11, 0.6)" }}>
                        {pickupInStore
                          ? t("Gratuit", "Free")
                          : (shippingCost !== null ? formatPrice(shippingCost) : "—")
                        }
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-3 pt-3 border-t" style={{ borderColor: "rgba(130, 57, 31, 0.06)" }}>
                      <span style={{ color: "rgba(13, 12, 11, 0.5)" }}>Total</span>
                      <span style={{ color: "#E1593F" }}>
                        {shippingCost !== null
                          ? formatPrice(totalPrice + shippingCost)
                          : formatPrice(totalPrice)
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t" style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}>
                  <button
                    type="submit"
                    disabled={processing}
                    className="w-full py-4 text-white text-xs tracking-[0.2em] uppercase transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-wait"
                    style={{ backgroundColor: "#E1593F" }}
                    onMouseEnter={e => { if (!processing) e.currentTarget.style.backgroundColor = "#C4462E" }}
                    onMouseLeave={e => { if (!processing) e.currentTarget.style.backgroundColor = "#E1593F" }}
                  >
                    {processing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t("Traitement en cours...", "Processing...")}
                      </>
                    ) : (
                      `${t("Confirmer", "Confirm")} \u2014 ${formatPrice(shippingCost !== null ? totalPrice + shippingCost : totalPrice)}`
                    )}
                  </button>
                  <p className="text-[10px] text-center mt-3" style={{ color: "rgba(13, 12, 11, 0.2)" }}>
                    {t("Paiement sécurisé \u2022 Vos données sont protégées", "Secure payment \u2022 Your data is protected")}
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
